import { Button, Skeleton } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnonymizerConfig } from '@/entities/anonymizer-config';
import { PhoneConfigForm } from './forms/PhoneConfigForm';
import { FioConfigForm } from './forms/FioConfigForm';
import { DictionariesConfigForm } from './forms/DictionariesConfigForm';
import { BirthDateConfigForm } from './forms/BirthDateConfigForm';
import { PassportConfigForm } from './forms/PassportConfigForm';
import { FinancialDocConfigForm } from './forms/FinancialDocConfigForm';
import { BenchmarkTestingForm } from './forms/BenchmarkTestingForm';
import type { AnonymizerClassName } from '@/entities/anonymizer-config';

type Props = {
  className?: string;
  onUnsavedChange?: (dirty: boolean) => void;
};

const FINANCIAL_CLASSES = ['snils', 'inn', 'bank-card', 'bank-account'];

export const AnonymizerConfigCard: FC<Props> = ({ className: classId, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const skipFetch = classId === 'benchmark-testing';
  const { data, isLoading, isError, refetch } = useAnonymizerConfig(skipFetch ? undefined : classId);

  if (!classId) {
    return <div className="flex items-center justify-center h-full text-muted-foreground">{t('selectClass')}</div>;
  }

  if (classId === 'benchmark-testing') {
    return <BenchmarkTestingForm />;
  }

  if (isLoading) {
    return <Skeleton className="h-full" />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <span>{t('selectClass')}</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {t('reset')}
        </Button>
      </div>
    );
  }

  if (FINANCIAL_CLASSES.includes(classId)) {
    return <FinancialDocConfigForm classId={classId} data={data} onUnsavedChange={onUnsavedChange} />;
  }

  switch (classId as AnonymizerClassName) {
    case 'phone':
      return <PhoneConfigForm data={data} onUnsavedChange={onUnsavedChange} />;
    case 'fio':
      return <FioConfigForm data={data} onUnsavedChange={onUnsavedChange} />;
    case 'fio/dictionaries':
      return <DictionariesConfigForm data={data} onUnsavedChange={onUnsavedChange} />;
    case 'birth-date':
      return <BirthDateConfigForm data={data} onUnsavedChange={onUnsavedChange} />;
    case 'passport':
      return <PassportConfigForm data={data} onUnsavedChange={onUnsavedChange} />;
    default:
      return <div className="flex items-center justify-center h-full text-muted-foreground">{t('selectClass')}</div>;
  }
};
